/**
 * Amazing Tales story examples for few-shot learning
 * These examples guide the AI to produce high-quality story elements
 */

export const EXAMPLE_PREAMBLE = `## Example Output Quality (from Amazing Tales)

These examples demonstrate the expected quality and structure for generated story elements.
Follow these patterns for interconnected, skill-rich storytelling appropriate for children.

IMPORTANT: These examples are in English for illustration only. You MUST generate all content (names, roles, descriptions, skill uses) in the requested language. Adapt character names to feel natural in the target language — do NOT use English names when the requested language is different.
`;

export const OBJECTIVE_EXAMPLES = `${EXAMPLE_PREAMBLE}
### Deep Dark Wood Theme Example

**Main Objective:** "Rescue the baby owl kidnapped by a wicked witch from her thorny tower"
- Emotional stakes: Rescuing a baby creates urgency and empathy
- Clear antagonist: Wicked witch provides a tangible obstacle
- Specific challenge: Thorny tower requires skill application
- Twist ideas: "The witch is lonely and sees the owl as her pet" OR "The owl is a cursed prince needing reversal"
- Skill applications: "Fly quickly to dodge thorns", "Do magic to shrink the tower door", "Talk to animals for owl's location clues"

---

### Pirate Seas Theme Example

**Main Objective:** "Recover the Spider Queen's stolen silk cloak from pirate raiders"
- Emotional stakes: Return precious item to its rightful owner
- Clear antagonist: Pirate raiders create maritime adventure
- Specific challenge: Naval pursuit and boarding
- Twist ideas: "Pirates are cursed puppets of the queen's uncle" OR "The cloak hides a map to buried treasure"
- Skill applications: "Swimming well to board the ship", "Fighting monsters against shark guards", "Seeking treasure for hidden clues"

---

### Magical Kingdoms Theme Example

**Main Objective:** "Climb the Frost King's peak to stop his eternal snowstorm on the village"
- Emotional stakes: Save village from freezing
- Clear antagonist: Frost King and his magic
- Specific challenge: Mountain climbing in harsh conditions
- Twist ideas: "The king is frozen by his own spell" OR "Villagers secretly caused it by stealing his ice crown"
- Skill applications: "Climbing high to scale icy cliffs", "Doing magic to melt paths", "Being brave against snow beasts"
`;

export const SIDEQUEST_EXAMPLES = `${EXAMPLE_PREAMBLE}
### Deep Dark Wood Theme Example

**Sidequest 1:** "Clear beaver dam flooding rabbit homes"
- Purpose: To gain rabbit allies
- Skill uses: "heave the jammed logs aside before the burrow collapses (being strong)", "spot the fox decoy branch that restarts the flood (being clever)"

**Sidequest 2:** "Free hedgehogs turned into frogs by a confused wizard"
- Purpose: Break the spell on innocent creatures
- Skill uses: "trace the spell circle and reverse the transformation chant (doing magic)", "enter the echoing cave while illusions try to scare you away (being brave)"

---

### Pirate Seas Theme Example

**Sidequest 1:** "Repair a sabotaged friendly ship"
- Purpose: Befriend hidden mechanics
- Skill uses: "earn the mechanic crew trust by sharing your rescue plan (being friendly)", "patch three torn sails before the tide turns (being handy)"

**Sidequest 2:** "Steal a key from Captain Dread's parrot"
- Purpose: Unlock the treasure hold
- Skill uses: "crawl across the moonlit rigging to reach the parrot nest (sneaking quietly)", "trade shiny buttons for the key with the stubborn parrot (talking to animals)"

---

### Magical Kingdoms Theme Example

**Sidequest 1:** "Gather herbs from goblin mines for warming potion"
- Purpose: Create protection against cold
- Skill uses: "find frostbloom herbs hidden behind false gem seams (seeking treasure)", "win a tunnel skirmish against goblins guarding the herb cart (fighting monsters)"

**Sidequest 2:** "Convince frost fairies to lend wings"
- Purpose: Gain flight ability to reach the peak
- Skill uses: "swap campfire legends to earn the fairies trust (being friendly)", "race through ice arches to prove you can fly with them (flying quickly)"
`;

export const SKILL_EXAMPLES = `${EXAMPLE_PREAMBLE}
### Deep Dark Wood Theme Example

**Skills:**
- "Flying Quickly" 🦅 - Application: "Dart through tangled branches to scout witch's lair or evade wolf packs"
- "Talking to Animals" 🐿️ - Application: "Rally squirrels for distractions or beavers for dam-building intel"
- "Doing Magic" ✨ - Application: "Conjure glowing lights in caves or heal wounded forest friends"
- "Being Brave" 💪 - Application: "Stare down trolls blocking paths or enter haunted thickets"

---

### Pirate Seas Theme Example

**Skills:**
- "Swimming Well" 🏊 - Application: "Dive for sunken clues or outswim pursuing sharks"
- "Fighting Monsters" ⚔️ - Application: "Duel kraken tentacles or pirate rivals in shipboard brawls"
- "Seeking Treasure" 💎 - Application: "Decipher X-marks from faded maps or spot doubloons in wrecks"
- "Being Clever" 🧠 - Application: "Rig pulley traps on enemy ships or decode parrot squawks"

---

### Magical Kingdoms Theme Example

**Skills:**
- "Climbing High" 🧗 - Application: "Scale castle walls or vine towers during sieges"
- "Being Strong" 💪 - Application: "Lift fallen drawbridges or hurl boulders at dragons"
- "Being Friendly" 🤝 - Application: "Charm grumpy dwarves for potion recipes or knights for alliances"
- "Being Handy" 🔨 - Application: "Forge magical keys or repair enchanted bridges"
`;

export const SETTING_EXAMPLES = `${EXAMPLE_PREAMBLE}
### Knights and Dragons Theme - Setting Variants

1. **Mountain Kingdom** - "A realm of foggy peaks and dragon caves in an age of old crowns"
2. **Enchanted Woods** - "A twilight forest where moving trees hide fairy paths and lost relics"
3. **Crumbling Castle** - "A haunted fortress at the edge of winter where ancient oaths still echo"

---

### Alien Planet Theme - Setting Variants

1. **Crystal Plains** - "Endless glowing fields where creatures communicate through color and light"
2. **Orbital Outpost** - "A lonely station above cloud storms during a fragile peace mission"
3. **Jungle World** - "Bioluminescent canopies over buried ruins from a vanished civilization"

---

### Setting Pattern

- Keep each setting short and vivid
- Focus on WHERE and WHEN, with tone and atmosphere
- Do not include main quest details, twists, or named heroes
`;

export const CHARACTER_EXAMPLES = `${EXAMPLE_PREAMBLE}
### Deep Dark Wood Theme Example

**Characters:**
- "Grizelda the Pedantic Witch" 🧙‍♀️ - Role: "Precise spellcaster who insists on exact ingredients for her gingerbread hut recipes"
  - Skill uses: "counter her perfectly-measured broomstorm curse in the candy lane (doing magic)", "offer to host the harvest feast with her to end her isolation (being friendly)"
- "Reynard the Cunning Fox" 🦊 - Role: "Sly trickster who always has three backup plans"
  - Skill uses: "outsmart the fox riddle trap at the forked trail sign (being clever)", "ask the sparrows to repeat what the fox told each animal (talking to animals)"
- "Bumbledor the Forgetful Wizard" 🧙 - Role: "Kind-hearted spell-misser who keeps losing his spectacles"
  - Skill uses: "repair his cracked lens so he can read the spellbook correctly (being handy)", "snatch the sparking wand while sparks bounce around the cave (being brave)"

---

### Pirate Seas Theme Example

**Characters:**
- "Captain Blackwood the Overconfident" 🏴‍☠️ - Role: "Boastful one-eyed raider who never believes he can lose"
  - Skill uses: "win a sword duel on the rain-slick deck at dawn (fighting monsters)", "steal his feathered hat during cannon thunder (sneaking quietly)"
- "Razorfin the Hungry Shark" 🦈 - Role: "Ferocious reef guardian who is always looking for his next meal"
  - Skill uses: "slip through coral arches before the shark circles back (swimming well)", "lure it away with a mirror-bright coin near the reef gate (seeking treasure)"
- "Wren the Nervous Mechanic" 🔧 - Role: "Shy ship fixer who flinches at every thunderclap"
  - Skill uses: "calm Wren during thunder so she reveals the drydock code (being friendly)", "haul the flooded tool chest onto the repair platform (being strong)"

---

### Magical Kingdoms Theme Example

**Characters:**
- "King Aldric the Calculating" 👑 - Role: "Icy monarch who plans every move three steps ahead"
  - Skill uses: "cast a thawing rune over the frozen throne chamber (doing magic)", "climb the icicle balcony to reach his storm scepter (climbing high)"
- "Grik the Enthusiastic Miner" ⛏️ - Role: "Wide-eyed gem-hoarder who gets wildly excited about every new rock"
  - Skill uses: "win his trust by solving the three-gem puzzle lock (being clever)", "spot the fake ruby that hides the real tunnel key (seeking treasure)"
- "Liora the Flighty Fairy" 🧚 - Role: "Winged helper who keeps darting off mid-sentence to chase butterflies"
  - Skill uses: "race the fairy through wind rings to retrieve the ice map (flying quickly)", "ask owl messengers for the old fairy oath story (talking to animals)"
`;

export const LOCATION_EXAMPLES = `${EXAMPLE_PREAMBLE}
### Deep Dark Wood Theme Example

**Locations:**
- "Goblin Mines" ⛏️ - "Stinking tunnels full of traps and glowing gems"
  - Skill uses: "sneak under rattling support beams before a cave-in starts (sneaking quietly)", "find the glow-gem cache hidden behind collapsed carts (seeking treasure)"
- "Thorn Tower" 🗼 - "Impenetrable brambles hiding the witch"
  - Skill uses: "cast a brief wilt charm to open a doorway through thorns (doing magic)", "hack a rescue path before the brambles close again (being strong)"
- "Rabbit Warrens" 🐰 - "Flooded burrows with hidden exits"
  - Skill uses: "swim through the narrow flood tunnel to reach trapped kits (swimming well)", "ask elder rabbits which tunnel leads behind the witch hut (talking to animals)"

---

### Pirate Seas Theme Example

**Locations:**
- "Warlock's Keep" 🏰 - "Foggy island tower where ships vanish"
  - Skill uses: "enter the keep at midnight while ghost bells ring (being brave)", "duel the phantom deckhands blocking the signal room (fighting monsters)"
- "Sunken Galleon" 🚢 - "Coral-crusted wreck with locked chests"
  - Skill uses: "dive through broken portholes before your lantern fades (swimming well)", "pick the rusted captain chest lock using wire and shell (being clever)"
- "Stormy Cove" ⛈️ - "Sheltered bay with echoing caves"
  - Skill uses: "climb the wet cliff ladder to the storm watch ledge (climbing high)", "find the hidden hoard by matching cave echoes to the map (seeking treasure)"

---

### Magical Kingdoms Theme Example

**Locations:**
- "Frost Peak" ⛰️ - "Slippery mountain with howling winds"
  - Skill uses: "climb the blue ice wall while wind gusts break your footing (climbing high)", "cast warmth runes on boots before crossing the frozen ridge (doing magic)"
- "Enchanted Forest Glade" 🌳 - "Talking trees guarding a pond"
  - Skill uses: "greet each guardian tree by its true name to pass safely (being friendly)", "fly across lily gaps before the water vines snap shut (flying quickly)"
- "Dragon's Lair" 🐉 - "Smoky cave atop cliffs"
  - Skill uses: "cross the flame corridor without retreating to reach the nest (being brave)", "roll a blocking boulder aside to open the rescue tunnel (being strong)"
`;

export const QUALITY_RULES = `## Key Quality Patterns to Follow

1. **Skills have concrete action applications**
   - NOT: "Use magic"
   - YES: "Shrink the tower door" or "Counter her curses"

2. **Twists add emotional depth**
   - Loneliness, misunderstandings, hidden truths, moral complexity
   - NOT: "There's a trap"
   - YES: "The witch is lonely and sees the owl as her pet"

3. **Sidequests provide tangible rewards**
   - Allies, items, information that helps the main quest
   - NOT: "Help someone"
   - YES: "Clear beaver dam flooding rabbit homes -> Gain rabbit allies"

4. **Characters ALWAYS have a proper name + a defining adjective + skill interactions**
   - Every character must have a proper name — never just a type like "Evil King" or "Shark Guard"
   - Names must feel natural in the target language (not always English)
   - Every character must have ONE defining adjective (nervous, pedantic, cunning, enthusiastic, etc.) that colors ALL their behavior
   - Use the "one-word character" technique: pick a single adjective and apply it to everything the character does
   - The role description should reflect the adjective in how they act
   - NOT: "Evil King" or "Shark Guard"
   - NOT: "Frost King - Icy monarch on his throne"
   - YES: "Name the Adjective + Type - Role that reflects the adjective"

5. **Locations have atmosphere + specific challenges**
   - Sensory details (stinking, foggy, slippery)
   - Concrete obstacles requiring specific skills
   - NOT: "A cave"
   - YES: "Goblin Mines - Stinking tunnels full of traps and glowing gems"

6. **Everything interconnects**
   - Objectives mention skills and locations
   - Sidequests reference characters and skills
   - Skills apply to multiple situations
   - Create a web of story possibilities

7. **Skill uses are specific actions tied to the exact element**
   - BAD: "fighting", "sneaking", "spotting treasure"
   - GOOD: "win a sword duel on the bridge", "steal the wizard hat during fireworks", "find the golden key in avalanche debris"
   - BAD: generic social filler like "convince the blacksmith to forge a shield"

8. **Each major element includes several skill ideas**
   - Main objective, sidequests, locations, characters, encounters: provide multiple concrete skill-use options
   - Skill uses should describe a scene-level action, not a broad ability label
`;

export const AMAZING_TALES_EXAMPLES = `${OBJECTIVE_EXAMPLES}

${SIDEQUEST_EXAMPLES}

${SKILL_EXAMPLES}

${SETTING_EXAMPLES}

${CHARACTER_EXAMPLES}

${LOCATION_EXAMPLES}

---

${QUALITY_RULES}`;
